import {
  selectUserLinks,
  selectUserRefs,
  selectUserTerms,
} from "Redux/UserSlice";
import { useAppDispatch, useAppSelector } from "Redux/hooks";
import { resetPopUpType, setClosePopUp, setPopupType, setProducerUserManualCreation, setSelectedTrigger, setShowPopUpOnly } from "feature/producer/actions";
import { ChangeEvent, useEffect, useState } from "react";
import {
  Link,
  LinkId,
  Reference,
  ReferenceId,
  Term,
  TermId,
} from "types/types";
import Searcher from "../../Logic/Searcher";
import {
  TriggerVariation,
  selectCurrentModule,
  selectLinkToAdd,
  selectPopUpType,
  selectRefToAdd,
  selectSearchBarShowing,
  selectTermToAdd,
  selectTriggerToAdd,
  selectTriggers,
  setSearchBarShowing,
  selectShowPopUp,
  createUserManual,
  selectUserManualCreation,
} from "../../Redux/ProducerSlice";
import ProducerCard from "../Atoms/ProducerCard";
import ProducerSearchSelector from "../Atoms/ProducerSearchSelector";
import RichTextReader from "../Atoms/RichTextReader";
import SearchShower from "../Atoms/SearchShower";
import { CoreEditor } from "./MinimalEditor";
import Button from "Components/Atoms/Button";
import { selectStyle } from "Redux/appSlice";
import Tooltip from "Components/Atoms/Tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfo } from "@fortawesome/free-solid-svg-icons";
import { useParams } from "react-router-dom";

function generateTriggers({
  triggers,
}: {
  triggers: Array<{ trigger: TriggerVariation }>;
}) {
  return {
    triggers: triggers.map(({ trigger }) => {
      return {
        label: trigger.label,
        trigger: trigger.trigger,
        answer: trigger.answer,
      };
    }),
  };
}

function generateLinks({ links }: { links: Record<LinkId, Link> }) {
  return {
    links: Object.keys(links).map((linkKey) => {
      return {
        name: links[linkKey].name,
        address: links[linkKey].address,
        description: links[linkKey].description,
        linkId: linkKey,
      };
    }),
  };
}

function generateTerms({ terms }: { terms: Record<TermId, Term> }) {
  return {
    terms: Object.keys(terms).map((termKey) => {
      return {
        name: terms[termKey].name,
        description: terms[termKey].description,
        termId: termKey,
      };
    }),
  };
}

function generateReferences({
  refs,
}: {
  refs: Record<ReferenceId, Reference>;
}) {
  return {
    refs: Object.keys(refs).map((refKey) => {
      return {
        code: refs[refKey].code,
        description: refs[refKey].description,
        link: refs[refKey].link,
        refId: refKey,
      };
    }),
  };
}

/**
   @function SearchBar
   search bar, search for triggers, links, ref, terms
*/
export default function SearchBar() {
  const userTerms = useAppSelector(selectUserTerms);
  const userLinks = useAppSelector(selectUserLinks);
  const userRefs = useAppSelector(selectUserRefs);
  const triggers = useAppSelector(selectTriggers);
  const popUpType = useAppSelector(selectPopUpType);
  const termToAdd = useAppSelector(selectTermToAdd);
  const linkToAdd = useAppSelector(selectLinkToAdd);
  const refToAdd = useAppSelector(selectRefToAdd);
  const triggerToAdd = useAppSelector(selectTriggerToAdd);
  const currentModule = useAppSelector(selectCurrentModule);
  const searchBarShowing = useAppSelector(selectSearchBarShowing);
  const dispatch = useAppDispatch();
  const [inputValue, setInputValue] = useState("");
  const { color1, color2 } = useAppSelector(selectStyle);
  const showPopUp = useAppSelector(selectShowPopUp);
  const params = useParams();
  const { orgId = "", moduleId = "" } = params;
  const userManualCreation = useAppSelector(selectUserManualCreation);

  useEffect(() => {
    setInputValue("");
    const input = document.getElementById("searchBarInput");
    if (input) {
      // TODO: what is this for?
      // input.value = "";
    }
    if(popUpType == "Link" || popUpType == "Term") {
      document.getElementById("searchBarInput")?.focus();
    }
  }, [popUpType]);

  useEffect(() => {
    dispatch(setProducerUserManualCreation({flag: false}));
  }, [termToAdd, linkToAdd, refToAdd])

  const [searcher] = useState(
    new Searcher({
      ...generateTriggers({
        triggers: triggers ? triggers : [],
      }),
      ...generateLinks({
        links: userLinks ? userLinks : {},
      }),
      ...generateReferences({
        refs: userRefs ? userRefs : {},
      }),
      ...generateTerms({
        terms: userTerms ? userTerms : {},
      }),
    })
  );

  useEffect(() => {
    searcher.addTrigger(
      generateTriggers({
        triggers: triggers ? triggers : [],
      }).triggers
    );
  }, [triggers]);

  useEffect(() => {
    searcher.addLink(
      generateLinks({
        links: userLinks ? userLinks : {},
      }).links
    );
  }, [userLinks]);

  useEffect(() => {
    searcher.addRef(
      generateReferences({
        refs: userRefs ? userRefs : {},
      }).refs
    );
  }, [userRefs]);

  useEffect(() => {
    searcher.addTerm(
      generateTerms({
        terms: userTerms ? userTerms : {},
      }).terms
    );
  }, [userTerms]);

  const [searchResult, setSearchResult] = useState<any[]>([]);
  const [showResult, setShowResult] = useState(false);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (triggers) {
      searcher.addTrigger(generateTriggers({ triggers: triggers }).triggers);
    }
  }, [triggers]);

  useEffect(() => {
    if (searchResult.length > 0) {
      dispatch(setSearchBarShowing(true));
    } else {
      dispatch(setSearchBarShowing(false));
    }
  }, [searchResult]);

  const onElementClick = (type: string, val: any) => {
    if (type === "Term") {
      if(userManualCreation) {
        dispatch(createUserManual({orgId, moduleId, termId: val.doc.termId}));
        dispatch(setProducerUserManualCreation({flag: false}));
      } else {
        termToAdd?.callback(val.doc.termId, val.doc);
      }
      setSearchResult([]);
      dispatch(resetPopUpType());
    }
    if (type === "Link") {
      linkToAdd?.callback(val.doc.linkId, val.doc);
      setSearchResult([]);
      dispatch(resetPopUpType());
    }
    if (type === "Ref") {
      refToAdd?.callback(val.doc.refId, val.doc);
      setSearchResult([]);
    }
  };

  useEffect(() => {
    setShowResult(searchBarShowing);
  }, [searchBarShowing]);

  const compressResults = (found: any) => {
    const compressed: Record<string, any> = {};

    found.forEach((f: any) => {
      if (compressed[f.type] === undefined) {
        compressed[f.type] = {};
      }
      f.el.result.forEach((r: any) => {
        if (compressed[f.type][r.id] === undefined) {
          compressed[f.type][r.id] = r.doc;
        }
      });
    });
    return compressed;
  };

  const onTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    const found = searcher.find(text, filter) || {};
    setShowResult(true);
    setInputValue(text);
    dispatch(setClosePopUp());
    if (found.length !== 0) {
      setSearchResult(() => {
        const compressed = compressResults(found);

        return Object.keys(compressed).flatMap((el) => {
          if (el === "trigger") {
            const trigger = compressed[el];

            if (triggerToAdd !== null) {
              Object.keys(trigger).forEach((t) => {
                let isBefore = false;

                currentModule?.chapters.forEach((c, c_i) => {
                  if (c.chapter_id === trigger[t].trigger.chapter) {
                    if (triggerToAdd && c_i < triggerToAdd.chapter) {
                      isBefore = true;
                    }

                    if (c_i == triggerToAdd?.chapter) {
                      c.questions.forEach((q, q_i) => {
                        if (
                          triggerToAdd &&
                          q_i < triggerToAdd.step &&
                          q.question_id === trigger[t].trigger.question
                        ) {
                          isBefore = true;
                        }
                      });
                    }
                  }
                });
                if (!isBefore) {
                  delete trigger[t];
                }
              });
            }

            return Object.keys(trigger).map((toRenderKey, i) => {
              const toRender = { doc: trigger[toRenderKey] } as const;

              return (
                <ProducerCard
                  key={`trigger${i}`}
                  chapter={toRender.doc.label}
                  label={
                    JSON.parse(toRender.doc.answer).text ? (
                      <RichTextReader
                        text={JSON.parse(toRender.doc.answer).text}
                        justText={true}
                      />
                    ) : (
                      <CoreEditor
                        text={JSON.parse(toRender.doc.answer)}
                        contentEditable={false}
                      />
                    )
                  }
                  onClick={() => {
                    dispatch(
                      setSelectedTrigger({
                        trigger: toRender.doc,
                        // callback: () => {
                        //   setInputValue("");
                        //   setSearchResult([]);
                        // },
                      })
                    );
                  }}
                  trigger={toRender.doc.trigger}
                />
              );
            });
          }
          if (el === "link") {
            const link = compressed[el];
            return Object.keys(link).map((toRenderKey, i) => {
              const toRender = { doc: link[toRenderKey] } as const;
              return (
                <ProducerCard
                  key={`link${i}`}
                  chapter={toRender.doc.name}
                  label={toRender.doc.address}
                  onClick={() => onElementClick("Link", toRender)}
                />
              );
            });
          }
          if (el === "ref") {
            const ref = compressed[el];
            return Object.keys(ref).map((toRenderKey, i) => {
              const toRender = { doc: ref[toRenderKey] } as const;
              return (
                <ProducerCard
                  key={`ref${i}`}
                  chapter={toRender.doc.code}
                  label={toRender.doc.address}
                  onClick={() => onElementClick("Ref", toRender)}
                />
              );
            });
          }
          if (el === "term") {
            const term = compressed[el];
            return Object.keys(term).map((toRenderKey, i) => {
              const toRender = { doc: term[toRenderKey] } as const;
              return (
                <ProducerCard
                  key={`term${i}`}
                  chapter={toRender.doc.name}
                  label={
                    <RichTextReader
                      text={JSON.parse(toRender.doc.description).text}
                      justText={true}
                    />
                  }
                  onClick={() => onElementClick("Term", toRender)}
                  term={true}
                />
              );
            });
          }
        });
      });
    } else {
      setSearchResult([]);
    }
  };

  const idTranslator = {
    Term: "terms",
    Link: "links",
    Reference: "refs",
    Trigger: "triggers",
  };

  return (
    <div className=" ">
      <div className="inline-flex">
        <input
          id="searchBarInput"
          type="text"
          className="h-fit rounded-md border bg-transparent py-1 px-5 text-white focus:outline-none"
          onChange={onTextChange}
          value={inputValue}
          defaultValue={inputValue}
          placeholder="search"
        />
        <ProducerSearchSelector
          voices={[
            { label: "all", id: "selector_All" },
            { label: "triggers", id: "triggers" },
            { label: "links", id: "links" },
            { label: "terms", id: "terms" },
            { label: "refs", id: "refs" },
          ].filter((o) => !userManualCreation || (o.id === "terms" && userManualCreation))}
          onClick={(id: any) => setFilter(id)}
          value={userManualCreation ? idTranslator["Term"] : ( popUpType ? idTranslator[popUpType] : "")}
        />
        {(popUpType == "Link" || popUpType == "Term") && !showPopUp && !userManualCreation && <Button
          textColor={color1}
          label={`Create ${popUpType}`}
          onClick={() => {
            setInputValue("");
            setSearchResult([]);
            dispatch(
              setShowPopUpOnly()
            );
          }
          }
          className= "px-12 py-2 rounded-full font-bold hover:bg-lime-400 focus:bg-lime-400 focus:outline-none active:bg-lime-600 transition-colors duration-150 text-sm !p-2 !m-auto col-start-3 col-end-4"
        />}
        <Button
          textColor={color1}
          label={`${currentModule?.user_manual_term_id && currentModule?.user_manual_term_id != "" ? "Change" : "Add"} Manual`}
          onClick={() => {
            dispatch(setProducerUserManualCreation({ flag: true }));
            dispatch(setPopupType());
            setTimeout(() => document.getElementById("searchBarInput")?.focus(), 300);
          }
          }
          className="px-12 py-2 rounded-full font-bold hover:bg-lime-400 focus:bg-lime-400 focus:outline-none active:bg-lime-600 transition-colors duration-150 text-sm !p-2 !m-auto col-start-3 col-end-4"
        />
        {currentModule?.user_manual_term_id && currentModule?.user_manual_term_id != "" && <Button
          textColor={color1}
          label={"Delete Manual"}
          onClick={() => {
            dispatch(createUserManual({ orgId, moduleId, termId: "" }));
          }
          }
          className="px-12 py-2 rounded-full font-bold hover:bg-lime-400 focus:bg-lime-400 focus:outline-none active:bg-lime-600 transition-colors duration-150 text-sm !p-2 !m-auto col-start-3 col-end-4"
        />}
      </div>
      {showResult && <SearchShower pageSize={5} cards={searchResult} setShowResult={setShowResult}></SearchShower>}
    </div>
  );
}
